type SupabaseErrorLike = {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
};

function errorText(raw: unknown): string {
  if (typeof raw === "string") return raw;
  const err = (raw || {}) as SupabaseErrorLike;
  const fromFields = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`.trim();
  if (fromFields) return fromFields;
  if (raw instanceof Error) return raw.message;
  return "";
}

function errorMessageLower(raw: unknown): string {
  return errorText(raw).toLowerCase();
}

const INTERNAL_PG_CODES = new Set([
  "23502",
  "23503",
  "23505",
  "23514",
  "22P02",
  "42P01",
  "42703",
  "PGRST204",
]);

/** Erros de permissão / RLS do Postgres ou PostgREST. */
export function isSupabasePermissionError(raw: unknown): boolean {
  const err = (raw || {}) as SupabaseErrorLike;
  const message = errorMessageLower(raw);
  return (
    err.code === "42501" ||
    err.code === "PGRST301" ||
    message.includes("permission denied") ||
    message.includes("permission_denied") ||
    message.includes("violates row-level security")
  );
}

/** Mensagens internas do banco que não devem ir para o usuário. */
function looksLikeInternalDbMessage(message: string): boolean {
  const t = message.toLowerCase();
  return (
    t.includes("violates row-level security") ||
    t.includes("new row violates") ||
    t.includes("violates foreign key") ||
    t.includes("violates not-null") ||
    t.includes("violates check constraint") ||
    t.includes("duplicate key value") ||
    (t.includes("policy ") && t.includes("for table")) ||
    t.includes("pgrst") ||
    t.includes("schema cache") ||
    t.includes("operator does not exist") ||
    t.includes("infinite recursion") ||
    (t.includes("column") && t.includes("does not exist"))
  );
}

function humanizeAuthOrKnownMessage(messageLower: string): string | null {
  if (
    messageLower.includes("invalid login credentials") ||
    messageLower.includes("invalid_credentials")
  ) {
    return "E-mail ou senha inválidos.";
  }
  if (messageLower.includes("email not confirmed")) {
    return "Confirme seu e-mail antes de entrar.";
  }
  if (messageLower.includes("user already registered")) {
    return "Este e-mail já está cadastrado.";
  }
  if (messageLower.includes("password should be")) {
    return "A senha não atende aos requisitos de segurança.";
  }
  if (
    messageLower.includes("new password should be different") ||
    messageLower.includes("same password")
  ) {
    return "A nova senha deve ser diferente da atual.";
  }
  if (messageLower.includes("token has expired") || messageLower.includes("otp_expired")) {
    return "Este link expirou. Solicite um novo.";
  }
  return null;
}

export type HumanizeSupabaseErrorOptions = {
  /** Mensagem quando a operação falhar por permissão/RLS. */
  permissionMessage?: string;
  /** Mensagem genérica quando o erro for técnico ou desconhecido. */
  fallback?: string;
};

const DEFAULT_PERMISSION_MESSAGE =
  "Você não tem permissão para realizar esta ação.";
const DEFAULT_FALLBACK =
  "Não foi possível concluir a operação. Tente novamente.";

/**
 * Converte erros do Supabase/Postgres em texto seguro para exibir ao usuário.
 */
export function humanizeSupabaseError(
  raw: unknown,
  options?: HumanizeSupabaseErrorOptions
): string {
  const permissionMessage =
    options?.permissionMessage ?? DEFAULT_PERMISSION_MESSAGE;
  const fallback = options?.fallback ?? DEFAULT_FALLBACK;

  if (isSupabasePermissionError(raw)) {
    return permissionMessage;
  }

  const err = (raw || {}) as SupabaseErrorLike;
  if (err.code && INTERNAL_PG_CODES.has(err.code)) {
    return fallback;
  }

  const message = errorText(raw).trim();
  const messageLower = message.toLowerCase();
  const known = humanizeAuthOrKnownMessage(messageLower);
  if (known) return known;

  if (
    messageLower.includes("no_assign_funnel_configured") ||
    messageLower.includes("default_funnel_must_be") ||
    messageLower.includes("default_stage_must_belong")
  ) {
    return "Configure o funil padrão deste membro em Configurações → Equipe.";
  }
  if (messageLower.includes("member_not_found") || messageLower.includes("lead_not_found")) {
    return "Não foi possível encontrar o lead ou o membro. Atualize a página e tente novamente.";
  }

  if (!message || looksLikeInternalDbMessage(message)) {
    return fallback;
  }

  return message;
}
